// 데이터베이스 표의 상태·페이지네이션·필터·CRUD 로직(렌더와 분리한 단일 훅).
//
// ~20개의 useState와 목록 조회/색인 로드/디바운스 검색 이펙트를 그대로 보유한다(동작 보존).
// 렌더 컴포넌트(DatabaseSection)는 이 훅이 돌려주는 값만 그린다.

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
    addManualReading,
    deleteReading,
    listIndicators,
    listIngestBatches,
    listReadings,
    updateReading,
} from '~/api/monitoring'
import type {
    IngestBatch,
    MonitoringIndicator,
    MonitoringReading,
} from '~/api/types'

export const PAGE = 50

export function useReadingsTable(dashboardId: string, initialBatchId?: string) {
    const [rows, setRows] = useState<MonitoringReading[]>([])
    const [total, setTotal] = useState(0)
    const [offset, setOffset] = useState(0)
    const [labels, setLabels] = useState<Record<string, string>>({})
    const [indicators, setIndicators] = useState<MonitoringIndicator[]>([])
    const [batches, setBatches] = useState<IngestBatch[]>([])
    const [loading, setLoading] = useState(true)
    const [busyId, setBusyId] = useState<string | null>(null)
    const [editId, setEditId] = useState<string | null>(null)
    const [editValue, setEditValue] = useState('')
    // 수동 1건 추가(인라인 행)
    const [adding, setAdding] = useState(false)
    const [addSaving, setAddSaving] = useState(false)
    const [addFieldId, setAddFieldId] = useState('')
    const [addValue, setAddValue] = useState('')
    const [addMeasuredAt, setAddMeasuredAt] = useState('')
    // 필터
    const [fieldId, setFieldId] = useState('')
    const [dateFrom, setDateFrom] = useState('')
    const [dateTo, setDateTo] = useState('')
    const [q, setQ] = useState('')
    const [qApplied, setQApplied] = useState('')
    const [batchId, setBatchId] = useState(initialBatchId ?? '')

    const loadPage = useCallback(
        (off: number) => {
            setLoading(true)
            listReadings(dashboardId, {
                offset: off,
                limit: PAGE,
                fieldId: fieldId || undefined,
                dateFrom: dateFrom || undefined,
                dateTo: dateTo || undefined,
                q: qApplied || undefined,
                batchId: batchId || undefined,
            })
                .then((p) => {
                    setRows(p.items)
                    setTotal(p.total)
                })
                .catch(() => {
                    setRows([])
                    setTotal(0)
                })
                .finally(() => setLoading(false))
        },
        [dashboardId, fieldId, dateFrom, dateTo, qApplied, batchId]
    )

    useEffect(() => {
        listIndicators(dashboardId)
            .then((inds) => {
                setIndicators(inds)
                const m: Record<string, string> = {}
                for (const i of inds) {
                    m[i.fieldId] = `${i.nameKo}${i.unit ? ` (${i.unit})` : ''}`
                }
                setLabels(m)
            })
            .catch(() => setIndicators([]))
    }, [dashboardId])

    // 적재 배치 목록은 한 번만 불러온다(필터·출처 라벨용).
    useEffect(() => {
        listIngestBatches(dashboardId)
            .then(setBatches)
            .catch(() => setBatches([]))
    }, [dashboardId])

    // 배치 id → 짧은 라벨(파일명 없으면 종류·행수).
    const batchLabels = useMemo(() => {
        const m: Record<string, string> = {}
        for (const b of batches) {
            m[b.id] = b.filename
                ? b.filename
                : `${b.kind === 'image' ? '이미지' : 'CSV'} ${b.rowCount}행`
        }
        return m
    }, [batches])

    // 값 검색은 디바운스(350ms).
    useEffect(() => {
        const t = setTimeout(() => setQApplied(q), 350)
        return () => clearTimeout(t)
    }, [q])

    // 필터 변경 시 첫 페이지로.
    useEffect(() => {
        setOffset(0)
    }, [fieldId, dateFrom, dateTo, qApplied, batchId])

    useEffect(() => {
        loadPage(offset)
    }, [offset, loadPage])

    const hasFilter = Boolean(fieldId || dateFrom || dateTo || q || batchId)
    const resetFilters = () => {
        setFieldId('')
        setDateFrom('')
        setDateTo('')
        setQ('')
        setBatchId('')
    }

    const startEdit = (r: MonitoringReading) => {
        setEditId(r.id)
        setEditValue(r.value ?? '')
    }

    const saveEdit = async (r: MonitoringReading) => {
        setBusyId(r.id)
        try {
            await updateReading(dashboardId, r.id, {
                value: editValue,
                unit: r.unit ?? null,
            })
            setEditId(null)
            loadPage(offset)
        } finally {
            setBusyId(null)
        }
    }

    const remove = async (r: MonitoringReading) => {
        setBusyId(r.id)
        try {
            await deleteReading(dashboardId, r.id)
            // 마지막 페이지에서 마지막 1건을 지우면 이전 페이지로.
            if (rows.length === 1 && offset >= PAGE) setOffset(offset - PAGE)
            else loadPage(offset)
        } finally {
            setBusyId(null)
        }
    }

    const openAdd = () => {
        setAddFieldId('')
        setAddValue('')
        setAddMeasuredAt('')
        setAdding(true)
    }

    const cancelAdd = () => {
        setAdding(false)
    }

    const canSaveAdd = Boolean(addFieldId && addValue.trim()) && !addSaving

    const saveAdd = async () => {
        if (!addFieldId || !addValue.trim()) return
        setAddSaving(true)
        try {
            await addManualReading(dashboardId, {
                fieldId: addFieldId,
                value: addValue,
                measuredAt: addMeasuredAt || undefined,
            })
            setAdding(false)
            loadPage(0)
        } finally {
            setAddSaving(false)
        }
    }

    const from = total === 0 ? 0 : offset + 1
    const to = Math.min(offset + rows.length, total)

    return {
        rows,
        total,
        offset,
        setOffset,
        labels,
        indicators,
        loading,
        busyId,
        editId,
        setEditId,
        editValue,
        setEditValue,
        adding,
        addSaving,
        addFieldId,
        setAddFieldId,
        addValue,
        setAddValue,
        addMeasuredAt,
        setAddMeasuredAt,
        fieldId,
        setFieldId,
        dateFrom,
        setDateFrom,
        dateTo,
        setDateTo,
        q,
        setQ,
        batchId,
        setBatchId,
        batches,
        batchLabels,
        hasFilter,
        resetFilters,
        startEdit,
        saveEdit,
        remove,
        openAdd,
        cancelAdd,
        canSaveAdd,
        saveAdd,
        from,
        to,
    }
}
